import { useState } from "react";
import { ImageOff } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useDemo } from "@/store/DemoStore";
import { btn } from "@/components/common";

const field = "h-11 w-full rounded-xl border border-slate-200 bg-white px-3.5 text-base text-navy-900 outline-none focus:border-navy-500 focus:ring-2 focus:ring-navy-100 sm:text-sm";
const label = "mb-1.5 block text-xs font-medium text-slate-500";
const EMPTY = { name: "", code: "", type: "Cruise", destination: "", supplier: "", vessel: "", departure: "", returnDate: "", cover: "" };

export function NewGroupDialog({ open, onOpenChange }) {
  const { createGroup } = useDemo();
  const [f, setF] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: k === "code" ? e.target.value.replace(/[^a-zA-Z0-9-]/g, "").toLowerCase() : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const g = await createGroup(f);
    setSaving(false);
    if (g) { setF(EMPTY); onOpenChange(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[calc(var(--app-h,100dvh)-2rem)] w-[calc(100vw-1.5rem)] max-w-2xl overflow-y-auto rounded-2xl p-5 sm:p-7" data-testid="new-group-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl text-navy-900">Create Group</DialogTitle>
          <DialogDescription>Set up the group basics. You'll be able to add pricing, itinerary and publish the public page in a later step.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="grid gap-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <label><span className={label}>Group name</span>
              <input required className={field} value={f.name} onChange={set("name")} placeholder="e.g. Morris Family Caribbean 2027" data-testid="new-group-name" /></label>
            <label><span className={label}>Group code <span className="text-slate-400">(used for the public link)</span></span>
              <input required className={field} value={f.code} onChange={set("code")} placeholder="e.g. caribbean2027" data-testid="new-group-code" /></label>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <label><span className={label}>Type</span>
              <select className={field} value={f.type} onChange={set("type")} data-testid="new-group-type">
                <option value="Cruise">Cruise</option>
                <option value="Resort">Resort</option>
              </select></label>
            <label><span className={label}>Destination</span>
              <input className={field} value={f.destination} onChange={set("destination")} placeholder="e.g. Eastern Caribbean" data-testid="new-group-destination" /></label>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <label><span className={label}>Supplier</span>
              <input className={field} value={f.supplier} onChange={set("supplier")} placeholder="e.g. Royal Caribbean" data-testid="new-group-supplier" /></label>
            <label><span className={label}>{f.type === "Resort" ? "Resort" : "Ship"}</span>
              <input className={field} value={f.vessel} onChange={set("vessel")} placeholder={f.type === "Resort" ? "e.g. Sandals Royal Bahamian" : "e.g. Wonder of the Seas"} data-testid="new-group-vessel" /></label>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <label><span className={label}>Departure date</span>
              <input type="date" className={field} value={f.departure} onChange={set("departure")} data-testid="new-group-departure" /></label>
            <label><span className={label}>Return date</span>
              <input type="date" className={field} value={f.returnDate} onChange={set("returnDate")} data-testid="new-group-return" /></label>
          </div>

          <div>
            <span className={label}>Cover photo URL <span className="text-slate-400">(shown on the public group page)</span></span>
            <div className="flex items-stretch gap-3">
              <input className={field} value={f.cover} onChange={set("cover")} placeholder="https://images.unsplash.com/..." data-testid="new-group-cover" />
              <div className="grid h-11 w-16 shrink-0 place-items-center overflow-hidden rounded-xl border border-slate-200 bg-slate-50">
                {f.cover ? <img src={f.cover} alt="" referrerPolicy="no-referrer" className="h-full w-full object-cover" onLoad={(e) => { e.currentTarget.style.display = ""; }} onError={(e) => { e.currentTarget.style.display = "none"; }} /> : <ImageOff className="h-4 w-4 text-slate-300" />}
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={() => onOpenChange(false)} className={btn.secondary}>Cancel</button>
            <button disabled={saving} className={btn.primary} data-testid="new-group-submit">{saving ? "Creating…" : "Create group"}</button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
